// IndexedDB persistence, shared across all game types. `players` is one
// shared list (a player's name is the same regardless of which game they're
// playing). `games` holds finished-game records tagged with `gameType` so
// history/stats can later be filtered per game.

const DB_NAME = 'HalveItDB';
const DB_VERSION = 2; // v2 adds the `gameType` index on the games store

let db = null;
let canUseIndexedDB = true;

// Never rejects, and never hangs forever - every caller treats persistence
// as a nice-to-have, not a requirement, so this always settles with either
// the db or null:
//  - `indexedDB` can throw synchronously just being accessed in some
//    browsers/privacy modes (e.g. Safari private browsing) - caught below.
//  - an open request can also sit in "blocked" state indefinitely (never
//    firing onsuccess/onerror/onupgradeneeded at all) if another tab/
//    connection still holds an older DB version open when this one needs to
//    upgrade - there's no event for "give up", so a timeout is the only way
//    to guarantee this resolves.
export function initDB() {
    return new Promise((resolve) => {
        if (!canUseIndexedDB) {
            resolve(null);
            return;
        }

        let settled = false;
        const settle = (value) => {
            if (settled) return;
            settled = true;
            clearTimeout(timeoutId);
            resolve(value);
        };
        const timeoutId = setTimeout(() => {
            console.error('IndexedDB open timed out (likely blocked by another tab/connection)');
            settle(null);
        }, 3000);

        try {
            const request = indexedDB.open(DB_NAME, DB_VERSION);

            request.onerror = () => {
                console.error('Failed to open IndexedDB');
                settle(null);
            };

            request.onblocked = () => {
                console.error('IndexedDB open blocked by another open connection');
                // Not settled here - the request may still complete once the
                // other connection closes; the timeout above is the backstop.
            };

            request.onsuccess = (event) => {
                db = event.target.result;
                settle(db);
            };

            request.onupgradeneeded = (event) => {
                db = event.target.result;
                const upgradeTransaction = event.target.transaction;

                if (!db.objectStoreNames.contains('players')) {
                    db.createObjectStore('players', { keyPath: 'id' });
                }

                let gamesStore;
                if (!db.objectStoreNames.contains('games')) {
                    gamesStore = db.createObjectStore('games', { keyPath: 'id' });
                    gamesStore.createIndex('date', 'date', { unique: false });
                } else {
                    gamesStore = upgradeTransaction.objectStore('games');
                }
                if (!gamesStore.indexNames.contains('gameType')) {
                    gamesStore.createIndex('gameType', 'gameType', { unique: false });
                }
            };
        } catch (e) {
            console.error('IndexedDB unavailable', e);
            settle(null);
        }
    });
}

export function isIndexedDBAvailable() {
    return canUseIndexedDB && !!db;
}

export function generateGameId() {
    // Generate a unique ID using timestamp and random string
    const timestamp = Date.now();
    const randomStr = Math.random().toString(36).substring(2, 9);
    return `game_${timestamp}_${randomStr}`;
}

// `playerConfigs` is `{ [playerName]: { type: 'cpu', difficulty } }` - only
// Computer players get an entry (a plain human name has nothing to store),
// keyed by name same as everywhere else a player is identified. Optional so
// every existing call site (and every already-saved record, pre-dating CPU
// players) still works unchanged.
export function savePlayersToStorage(players, playerConfigs) {
    if (!isIndexedDBAvailable()) return;
    try {
        const transaction = db.transaction(['players'], 'readwrite');
        const store = transaction.objectStore('players');

        // Store players as a single record
        store.put({ id: 'playersList', players, playerConfigs: playerConfigs || {} });

        transaction.onerror = () => {
            console.error('Failed to save players');
        };
    } catch (e) {
        // Silently fail if storage is not available
    }
}

// Resolves with { players, playerConfigs }, or null if there is none / storage
// is unavailable. playerConfigs is always an object (empty when the saved
// record pre-dates CPU players, or has no CPU players in it).
export function loadPlayersFromStorage() {
    return new Promise((resolve) => {
        if (!isIndexedDBAvailable()) {
            resolve(null);
            return;
        }
        try {
            const transaction = db.transaction(['players'], 'readonly');
            const store = transaction.objectStore('players');
            const request = store.get('playersList');

            request.onsuccess = (event) => {
                const data = event.target.result;
                if (data && data.players && Array.isArray(data.players) && data.players.length > 0) {
                    resolve({ players: data.players, playerConfigs: data.playerConfigs || {} });
                } else {
                    resolve(null);
                }
            };

            request.onerror = () => {
                console.error('Failed to load players');
                resolve(null);
            };
        } catch (e) {
            // Silently fail if there's an error loading
            resolve(null);
        }
    });
}

export function saveGameToHistory({ gameId, gameType, gameTypeLabel, players, scores, standingLabels }) {
    if (!isIndexedDBAvailable()) return;
    try {
        const transaction = db.transaction(['games'], 'readwrite');
        const store = transaction.objectStore('games');

        // Create game record with unique ID
        const gameRecord = {
            id: gameId,
            gameType,
            // Overrides the game type's own fixed name in history/stats (e.g.
            // x01 reports the actual starting score, "301", not "501") - null
            // for every game that doesn't supply one.
            gameTypeLabel: gameTypeLabel || null,
            date: new Date().toISOString(),
            players: players.map(player => ({
                name: player,
                score: scores[player],
                // Same idea per-player (e.g. x01's "avg 82.3") - undefined
                // for games that don't supply standingLabels at all.
                label: standingLabels ? standingLabels[player] : undefined
            })).sort((a, b) => b.score - a.score)
        };

        // Add to IndexedDB
        store.add(gameRecord);

        transaction.onsuccess = () => {
            // Clean up old games if needed (keep last 50)
            cleanupOldGames();
        };

        transaction.onerror = () => {
            console.error('Failed to save game');
        };
    } catch (e) {
        // Silently fail
    }
}

export function cleanupOldGames() {
    if (!isIndexedDBAvailable()) return;

    const transaction = db.transaction(['games'], 'readwrite');
    const store = transaction.objectStore('games');
    const index = store.index('date');

    // Get all games sorted by date
    const request = index.openCursor(null, 'prev');
    let count = 0;

    request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
            count++;
            if (count > 50) {
                // Delete games beyond the 50 most recent
                store.delete(cursor.primaryKey);
            }
            cursor.continue();
        }
    };
}

// Resolves with all game records, newest first, or null on error.
export function getAllGames() {
    return new Promise((resolve) => {
        if (!isIndexedDBAvailable()) {
            resolve(null);
            return;
        }
        try {
            const transaction = db.transaction(['games'], 'readonly');
            const store = transaction.objectStore('games');
            const index = store.index('date');

            const request = index.openCursor(null, 'prev');
            const games = [];

            request.onsuccess = (event) => {
                const cursor = event.target.result;
                if (cursor) {
                    games.push(cursor.value);
                    cursor.continue();
                } else {
                    resolve(games);
                }
            };

            request.onerror = () => {
                resolve(null);
            };
        } catch (e) {
            resolve(null);
        }
    });
}
